'use strict';

function isNameReserved(name) {
  const reservedNames = [
    // Keywords
    "break",
    "case",
    "catch",
    "class",
    "const",
    "continue",
    "debugger",
    "default",
    "delete",
    "do",
    "else",
    "export",
    "extends",
    "finally",
    "for",
    "function",
    "if",
    "import",
    "in",
    "instanceof",
    "new",
    "return",
    "super",
    "switch",
    "this",
    "throw",
    "try",
    "typeof",
    "var",
    "void",
    "while",
    "with",
    "yield",
    // Future reserved words
    "enum",
    // Strict mode reserved words
    "implements",
    "interface",
    "let",
    "package",
    "private",
    "protected",
    "public",
    "static",
    // Contextual keywords
    "async",
    "await",
    "constructor",
    // Literals
    "null",
    "true",
    "false"
  ];
  return reservedNames.includes(name);
}
function sanitizeIdentifier(identifier) {
  const sanitized = identifier.replace(/[^a-zA-Z0-9_$]/g, "_");
  if (isNameReserved(sanitized)) {
    return sanitized + "_";
  }
  if (/^\d/.test(sanitized)) {
    return "_" + sanitized;
  }
  if (sanitized === "" || /^_+$/.test(sanitized)) {
    return "_unnamed";
  }
  return sanitized;
}
function escapeStringLiteral(str) {
  return str.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\n/g, "\\n").replace(/\r/g, "\\r").replace(/\u2028/g, "\\u2028").replace(/\u2029/g, "\\u2029");
}
function parseTypeFromTypeDef(typeDef, isFunctionInput = false) {
  switch (typeDef.type) {
    case "scSpecTypeVal":
      return "any";
    case "scSpecTypeBool":
      return "boolean";
    case "scSpecTypeVoid":
      return "null";
    case "scSpecTypeError":
      return "Error";
    case "scSpecTypeU32":
    case "scSpecTypeI32":
      return "number";
    case "scSpecTypeU64":
    case "scSpecTypeI64":
    case "scSpecTypeTimepoint":
    case "scSpecTypeDuration":
    case "scSpecTypeU128":
    case "scSpecTypeI128":
    case "scSpecTypeU256":
    case "scSpecTypeI256":
      return "bigint";
    case "scSpecTypeBytes":
    case "scSpecTypeBytesN":
      return "Uint8Array";
    case "scSpecTypeString":
      return "string";
    case "scSpecTypeSymbol":
      return "string";
    case "scSpecTypeAddress":
    case "scSpecTypeMuxedAddress": {
      if (isFunctionInput) {
        return "string | Address";
      }
      return "string";
    }
    case "scSpecTypeVec": {
      const vecType = parseTypeFromTypeDef(
        typeDef.value.elementType,
        isFunctionInput
      );
      return `Array<${vecType}>`;
    }
    case "scSpecTypeMap": {
      const keyType = parseTypeFromTypeDef(
        typeDef.value.keyType,
        isFunctionInput
      );
      const valueType = parseTypeFromTypeDef(
        typeDef.value.valueType,
        isFunctionInput
      );
      return `Map<${keyType}, ${valueType}>`;
    }
    case "scSpecTypeTuple": {
      const tupleTypes = typeDef.value.valueTypes.map(
        (t) => parseTypeFromTypeDef(t, isFunctionInput)
      );
      return `[${tupleTypes.join(", ")}]`;
    }
    case "scSpecTypeOption": {
      while (typeDef.value.valueType.type === "scSpecTypeOption") {
        typeDef = typeDef.value.valueType;
      }
      const optionType = parseTypeFromTypeDef(
        typeDef.value.valueType,
        isFunctionInput
      );
      return `${optionType} | null`;
    }
    case "scSpecTypeResult": {
      const okType = parseTypeFromTypeDef(
        typeDef.value.okType,
        isFunctionInput
      );
      const errorType = parseTypeFromTypeDef(
        typeDef.value.errorType,
        isFunctionInput
      );
      return `Result<${okType}, ${errorType}>`;
    }
    case "scSpecTypeUdt": {
      const udtName = sanitizeIdentifier(typeDef.value.name.toString());
      return udtName;
    }
    default:
      return "unknown";
  }
}
function extractNestedTypes(typeDef) {
  switch (typeDef.type) {
    case "scSpecTypeVec":
      return [typeDef.value.elementType];
    case "scSpecTypeMap":
      return [typeDef.value.keyType, typeDef.value.valueType];
    case "scSpecTypeTuple":
      return typeDef.value.valueTypes;
    case "scSpecTypeOption":
      return [typeDef.value.valueType];
    case "scSpecTypeResult":
      return [typeDef.value.okType, typeDef.value.errorType];
    default:
      return [];
  }
}
function visitTypeDef(typeDef, accumulator) {
  const typeSwitch = typeDef.type;
  switch (typeSwitch) {
    case "scSpecTypeUdt":
      accumulator.typeFileImports.add(
        sanitizeIdentifier(typeDef.value.name.toString())
      );
      return;
    case "scSpecTypeAddress":
    case "scSpecTypeMuxedAddress":
      accumulator.stellarImports.add("Address");
      return;
    case "scSpecTypeBytes":
    case "scSpecTypeBytesN":
      return;
    case "scSpecTypeVal":
      accumulator.stellarImports.add("xdr");
      return;
    case "scSpecTypeResult":
      accumulator.stellarContractImports.add("Result");
      break;
    // Primitive types that need no imports
    case "scSpecTypeBool":
    case "scSpecTypeVoid":
    case "scSpecTypeError":
    case "scSpecTypeU32":
    case "scSpecTypeI32":
    case "scSpecTypeU64":
    case "scSpecTypeI64":
    case "scSpecTypeTimepoint":
    case "scSpecTypeDuration":
    case "scSpecTypeU128":
    case "scSpecTypeI128":
    case "scSpecTypeU256":
    case "scSpecTypeI256":
    case "scSpecTypeString":
    case "scSpecTypeSymbol":
      return;
  }
  const nestedTypes = extractNestedTypes(typeDef);
  nestedTypes.forEach((nested) => visitTypeDef(nested, accumulator));
}
function generateTypeImports(typeDefs) {
  const imports = {
    typeFileImports: /* @__PURE__ */ new Set(),
    stellarContractImports: /* @__PURE__ */ new Set(),
    stellarImports: /* @__PURE__ */ new Set()
  };
  typeDefs.forEach((typeDef) => visitTypeDef(typeDef, imports));
  return imports;
}
function formatImports(imports, options) {
  const importLines = [];
  const typeFileImports = imports.typeFileImports;
  const stellarContractImports = [
    ...imports.stellarContractImports,
    ...options?.additionalStellarContractImports || []
  ];
  const stellarImports = [
    ...imports.stellarImports,
    ...options?.additionalStellarImports || []
  ];
  if (options?.includeTypeFileImports && typeFileImports.size > 0) {
    importLines.push(
      `import {${Array.from(typeFileImports).join(", ")}} from './types.js';`
    );
  }
  if (stellarContractImports.length > 0) {
    const uniqueContractImports = Array.from(new Set(stellarContractImports));
    importLines.push(
      `import {${uniqueContractImports.join(", ")}} from '@stellar/stellar-sdk/contract';`
    );
  }
  if (stellarImports.length > 0) {
    const uniqueStellarImports = Array.from(new Set(stellarImports));
    importLines.push(
      `import {${uniqueStellarImports.join(", ")}} from '@stellar/stellar-sdk';`
    );
  }
  return importLines.join("\n");
}
function escapeJSDocContent(text) {
  return text.replace(/\*\//g, "* /").replace(
    /@(?!(param|returns?|type|throws?|example|deprecated|see|link|since|author|version|description|summary)\b)/g,
    "\\@"
  );
}
function formatJSDocComment(comment, indentLevel = 0) {
  if (comment.trim() === "") {
    return "";
  }
  const indent = " ".repeat(indentLevel);
  const escapedComment = escapeJSDocContent(comment);
  const lines = escapedComment.split("\n").map((line) => `${indent} * ${line}`.trimEnd());
  return `${indent}/**
${lines.join("\n")}
${indent} */
`;
}
function toPascalCase(identifier) {
  const pascal = identifier.split(/[_$]+/).filter((part) => part.length > 0).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join("");
  return pascal === "" ? "Unnamed" : pascal;
}
function toCamelCase(identifier) {
  const pascal = toPascalCase(identifier);
  return pascal.charAt(0).toLowerCase() + pascal.slice(1);
}
function isTupleStruct(udtStruct) {
  const fields = udtStruct.fields;
  return fields.every(
    (field, index) => field.name.toString().trim() === index.toString()
  );
}

exports.escapeStringLiteral = escapeStringLiteral;
exports.formatImports = formatImports;
exports.formatJSDocComment = formatJSDocComment;
exports.generateTypeImports = generateTypeImports;
exports.isNameReserved = isNameReserved;
exports.isTupleStruct = isTupleStruct;
exports.parseTypeFromTypeDef = parseTypeFromTypeDef;
exports.sanitizeIdentifier = sanitizeIdentifier;
exports.toCamelCase = toCamelCase;
exports.toPascalCase = toPascalCase;
//# sourceMappingURL=utils.js.map
